import { Eye, Search, ArrowUpDown, ChevronDown } from "lucide-react";
import { Supplier } from "../../types/supplier";

interface SupplierTableProps {
  suppliers: Supplier[];
  onViewSupplier: (supplier: Supplier) => void;
  onEditSupplier?: (supplier: Supplier) => void;
  total: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  loading: boolean;
  companyTypes: any[];
}

export function SupplierTable({
  suppliers = [],
  onViewSupplier,
  loading = false,
  total = 0,
  currentPage = 1,
  pageSize = 25,
  onPageChange,
  companyTypes = [],
}: SupplierTableProps) {
  const safeSuppliers = Array.isArray(suppliers) ? suppliers : [];
  const totalCount = Number(total) || safeSuppliers.length || 0;
  const safePageSize = Number(pageSize) || 25;
  const safeCurrentPage = Number(currentPage) || 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / safePageSize));
  const startIndex = (safeCurrentPage - 1) * safePageSize;
  const endIndex = Math.min(startIndex + safePageSize, totalCount);

  return (
    <div className="bg-white border border-[#dde3e4] rounded-[10px] shadow-[0_1px_2px_rgba(21,27,29,0.04),0_4px_10px_-6px_rgba(21,27,29,0.10)] overflow-hidden font-['Poppins',sans-serif]">
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-[#dde3e4] border-t-[#3f8b98]" />
          </div>
        ) : safeSuppliers.length > 0 ? (
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#F1F6FA] text-[#2e6b76] border-b border-[#dde3e4] sticky top-0 z-10 text-[11px] font-semibold">
                <th className="py-3 pl-5 pr-2 w-[60px]">Logo</th>
                <th className="py-3 px-3 text-[11px] font-semibold">
                  <div className="flex items-center gap-1 cursor-pointer select-none hover:text-[#151b1d]">
                    <span>Supplier Details</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th className="py-3 px-3 text-[11px] font-semibold">Entity Type</th>
                <th className="py-3 px-3 text-[11px] font-semibold">Primary Contact</th>
                <th className="py-3 px-3 text-[11px] font-semibold">Trade Name</th>
                <th className="py-3 px-3 text-[11px] font-semibold">
                  <div className="flex items-center gap-1 cursor-pointer select-none hover:text-[#151b1d]">
                    <span>Onboarded</span>
                    <ChevronDown className="w-3.5 h-3.5 opacity-80" />
                  </div>
                </th>
                <th className="py-3 px-3 text-right text-[11px] font-semibold">
                  <div className="flex items-center justify-end gap-1 cursor-pointer select-none hover:text-[#151b1d]">
                    <span>Assets</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th className="py-3 px-3 text-right text-[11px] font-semibold">
                  <div className="flex items-center justify-end gap-1 cursor-pointer select-none hover:text-[#151b1d]">
                    <span>Invoices</span>
                    <ArrowUpDown className="w-3 h-3 opacity-60" />
                  </div>
                </th>
                <th className="py-3 pr-5 pl-3 text-right text-[11px] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eaeeef]">
              {safeSuppliers.map((supplier) => {
                const entityTypeName =
                  (companyTypes || []).find((type) => String(type.id) === String(supplier.company_type_id))?.name ||
                  supplier.company_type ||
                  "Private Limited";

                const spocList = supplier?.spoc_details || [];
                const primarySpocObj = spocList.find((spoc: any) => spoc.is_primary) || spocList[0];
                const extraContactsCount = Math.max(0, spocList.length - 1);
                const contactName = primarySpocObj?.name;

                const logoInitials = supplier.company_name
                  ? supplier.company_name
                      .split(" ")
                      .filter(Boolean)
                      .map((word) => word[0])
                      .slice(0, 2)
                      .join("")
                      .toUpperCase()
                  : "-";

                const onboardedFormatted = supplier.created_at
                  ? new Date(supplier.created_at).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric"
                    })
                  : "—";

                return (
                  <tr
                    key={supplier.id}
                    className="hover:bg-[#f9fcfc] transition-colors group text-[13px]"
                  >
                    {/* Logo Column */}
                    <td className="py-3.5 pl-5 pr-2 align-middle">
                      <div className="w-9 h-9 rounded-[8px] bg-[#F1F6FA] text-[#2e6b76] font-semibold text-xs flex items-center justify-center flex-shrink-0">
                        {supplier.company_logo ? (
                          <img
                            src={supplier.company_logo}
                            alt={supplier.company_name}
                            className="h-full w-full object-cover rounded-[8px]"
                          />
                        ) : (
                          <span>{logoInitials}</span>
                        )}
                      </div>
                    </td>

                    {/* Supplier Details Column */}
                    <td className="py-3.5 px-3 align-middle">
                      <div className="flex flex-col">
                        <span className="text-[13px] font-semibold text-[#151b1d] uppercase tracking-tight line-clamp-1">
                          {supplier.company_name || "-"}
                        </span>
                      </div>
                    </td>

                    {/* Entity Type Column */}
                    <td className="py-3.5 px-3 align-middle">
                      <span className="inline-block rounded-[6px] bg-[#eaeeef] text-[#526066] px-2.5 py-0.5 text-[11.5px] font-medium whitespace-nowrap">
                        {entityTypeName}
                      </span>
                    </td>

                    {/* Primary Contact Column */}
                    <td className="py-3.5 px-3 align-middle">
                      {contactName ? (
                        <div className="flex items-center">
                          <span className="text-[12.5px] font-semibold text-[#151b1d]">
                            {contactName}
                          </span>
                          {extraContactsCount > 0 && (
                            <span className="ml-1.5 inline-block text-[10px] font-medium bg-[#e1f0f1] text-[#2e6b76] px-1.5 py-0.2 rounded">
                              +{extraContactsCount} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#7c8b91] text-xs">—</span>
                      )}
                    </td>

                    {/* Trade Name Column */}
                    <td className="py-3.5 px-3 align-middle text-[12.5px] text-[#526066]">
                      {supplier.trade_name || "—"}
                    </td>

                    {/* Onboarded Date Column */}
                    <td className="py-3.5 px-3 align-middle text-[12.5px] text-[#526066] whitespace-nowrap">
                      {onboardedFormatted}
                    </td>

                    {/* Assets Count Column */}
                    <td className="py-3.5 px-3 align-middle text-right text-[13px] font-semibold text-[#151b1d]">
                      {supplier.total_assets ?? "0"}
                    </td>

                    {/* Invoices Count Column */}
                    <td className="py-3.5 px-3 align-middle text-right text-[13px] font-semibold text-[#151b1d]">
                      {supplier.total_proforma_invoices ?? "0"}
                    </td>

                    {/* Actions Column */}
                    <td className="py-3.5 pr-5 pl-3 align-middle text-right">
                      <div className="flex items-center justify-end">
                        <button
                          type="button"
                          onClick={() => onViewSupplier(supplier)}
                          className="w-7 h-7 rounded-[6px] text-[#526066] hover:text-[#3f8b98] hover:bg-[#e1f0f1] flex items-center justify-center transition-colors cursor-pointer"
                          title="View Supplier"
                          aria-label="View Supplier"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-[#7c8b91]">
            <Search className="h-8 w-8 opacity-30 mb-2" />
            <p className="text-sm font-semibold text-[#151b1d]">No suppliers found</p>
            <p className="text-xs text-[#7c8b91] mt-0.5">Try adjusting your search query</p>
          </div>
        )}
      </div>

      {/* Pagination Footer */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[#dde3e4] px-5 py-3 bg-white text-xs text-[#526066]">
        <div className="flex items-center gap-4 flex-wrap">
          <span>
            {totalCount > 0
              ? `Showing ${startIndex + 1}–${endIndex} of ${totalCount}`
              : safeSuppliers.length > 0
              ? `Showing 1–${safeSuppliers.length} of ${safeSuppliers.length}`
              : "Showing 0 of 0"}
          </span>

          <label className="flex items-center gap-1.5 text-xs text-[#526066]">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => onPageChange(1, Number(e.target.value))}
              className="h-7 px-2 rounded-[6px] border border-[#dde3e4] bg-white text-[#151b1d] text-xs outline-none focus:border-[#3f8b98] cursor-pointer"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => onPageChange(Math.max(1, currentPage - 1), pageSize)}
            disabled={currentPage <= 1}
            className="min-w-[28px] h-7 px-2 border border-[#dde3e4] rounded-[6px] bg-white text-[#151b1d] hover:border-[#3f8b98] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium cursor-pointer transition-colors"
            aria-label="Previous page"
          >
            ‹
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) pageNum = i + 1;
            else if (currentPage <= 3) pageNum = i + 1;
            else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
            else pageNum = currentPage - 2 + i;

            const isActive = currentPage === pageNum;
            return (
              <button
                key={pageNum}
                type="button"
                onClick={() => onPageChange(pageNum, pageSize)}
                className={`min-w-[28px] h-7 px-2 rounded-[6px] text-xs font-medium cursor-pointer transition-colors ${
                  isActive
                    ? "bg-[#3f8b98] border border-[#3f8b98] text-white font-semibold"
                    : "bg-white border border-[#dde3e4] text-[#151b1d] hover:border-[#3f8b98]"
                }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1), pageSize)}
            disabled={currentPage >= totalPages}
            className="min-w-[28px] h-7 px-2 border border-[#dde3e4] rounded-[6px] bg-white text-[#151b1d] hover:border-[#3f8b98] disabled:opacity-40 disabled:cursor-not-allowed text-xs font-medium cursor-pointer transition-colors"
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
